import type { ReactNode } from "react";
import { Check, Zap } from "lucide-react";
import { useTranslation } from "react-i18next";

interface PaywallPlanCardProps {
  planName: string;
  price: string;
  billingLabel: string;
  includedAiLabel: string;
  freeLabel: string;
  features: Array<string | PaywallFeature>;
  actions: ReactNode;
  secondaryAction?: ReactNode;
}

export interface PaywallFeature {
  freeIncluded?: boolean;
  label: string;
  value?: ReactNode;
}

export function PaywallPlanCard({
  planName,
  price,
  billingLabel,
  includedAiLabel,
  freeLabel,
  features,
  actions,
  secondaryAction,
}: PaywallPlanCardProps) {
  const { t } = useTranslation();
  return (
    <div className="w-full">
      <section aria-label={t("paywall.plan_aria", { plan: planName })}>
        <div className="flex items-center gap-4 rounded-t-[28px] border border-b-0 border-[var(--dp-brand-deep)] bg-[var(--dp-brand-deep)] px-6 pb-12 pt-6">
          <div className="min-w-0 flex-1">
            <p className="text-[18px] font-bold leading-[27px] text-[var(--dp-brand)]">
              {planName}
            </p>
            <div className="flex flex-wrap items-end gap-x-3">
              <p className="text-[36px] font-bold leading-[normal] tracking-[-0.09px] text-white">
                {price}
              </p>
              <p className="pb-1.5 text-[16px] font-medium leading-6 text-white">
                {billingLabel}
              </p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2 rounded-lg bg-white/10 p-2 text-white">
            <Zap aria-hidden="true" className="size-5" strokeWidth={1.8} />
            <span className="text-[14px] font-medium leading-[21px] tracking-[0.1px]">
              {includedAiLabel}
            </span>
          </div>
        </div>

        <div className="relative z-10 -mt-6 overflow-hidden rounded-[28px] border border-[var(--dp-brand-deep)] bg-white p-4 shadow-[0_4px_6px_-2px_rgba(0,0,0,0.05),0_10px_15px_-3px_rgba(0,0,0,0.1)]">
          <div className="relative overflow-hidden rounded-2xl">
            <div aria-hidden="true" className="absolute inset-y-px right-0 w-28 bg-[var(--dp-brand-soft)]" />
            <div className="relative grid grid-cols-[minmax(0,1fr)_96px_96px] items-center gap-2 px-3 py-1">
              <span aria-hidden="true" />
              <span className="py-1.5 text-center text-[16px] font-bold leading-6 text-[#202020]">
                {freeLabel}
              </span>
              <span className="py-1.5 text-center text-[16px] font-bold leading-6 text-[var(--dp-brand)]">
                {planName}
              </span>
            </div>

            {features.map((feature) => {
              const item = typeof feature === "string" ? { label: feature } : feature;
              return (
                <div
                  key={item.label}
                  className="relative grid grid-cols-[minmax(0,1fr)_96px_96px] items-center gap-2 border-t border-[#f2f3f2] p-4"
                >
                  <p className="min-w-0 pr-2 text-[16px] font-medium leading-6 text-[#0a0c11]">
                    {item.label}
                  </p>
                  <span className="flex items-center justify-center text-[13px] leading-normal text-[#c2c8c4]">
                    {item.freeIncluded ? (
                      <Check
                        aria-label={t("paywall.feature_included_free", { feature: item.label })}
                        className="size-4"
                        strokeWidth={2.2}
                      />
                    ) : (
                      "—"
                    )}
                  </span>
                  <span className="flex items-center justify-center py-[3px] text-[var(--dp-brand)]">
                    {item.value ?? (
                      <Check
                        aria-label={t("paywall.feature_included", { feature: item.label })}
                        className="size-4"
                        strokeWidth={2.2}
                      />
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <div className="mt-8 flex flex-col gap-3">
        {actions}
        {secondaryAction}
      </div>
    </div>
  );
}
